import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  inject
} from "@angular/core";
import { NgTemplateOutlet } from "@angular/common";
import { ModalController, Platform, IonAccordion, IonAccordionGroup, IonButton, IonCheckbox, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonPopover, IonSearchbar, IonSelect, IonSelectOption, IonText, IonTitle, IonToolbar } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService, TranslatePipe } from "@ngx-translate/core";
import {
  ColumnMode,
  DatatableComponent,
  NgxDatatableModule,
  SelectionType,
} from "@swimlane/ngx-datatable";
import { NoContentComponent } from "src/components/shared-components/components/general-components/no-content/no-content.component";
import { PagerComponent } from "src/components/shared-components/components/general-components/pager/pager.component";
import { from, Observable, of, Subject, Subscription } from "rxjs";
import { catchError, filter, switchMap, take, tap } from "rxjs/operators";
import { Address } from "src/components/shared-components/class/component-classes/address";
import { iAddressSearch } from "src/components/shared-components/interfaces/iAddress-search";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { GenericStoreApiData } from "src/components/shared-components/interfaces/generic-store-api-data";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { AddressService } from "src/service/address.service";
import { AlertService } from "src/service/alert.service";
import { EventService } from "src/service/event.service";
import { GatewayService } from "src/service/gateway.service";
import { LoaderService } from "src/service/loader.service";
import { LoggerService } from "src/service/logger.service";
import { NetworkService } from "src/service/network.service";
import { ThemeService } from "src/service/theme.service";
import { listGateway } from "src/state/selectors/gateway.selectors";
import { FiltersType } from "src/components/shared-components/enum/enumFilters";
import {
  Column,
  TableInfo,
} from "src/components/shared-components/class/general-classes/item-history";
import {
  ColumnTableType,
  HideShowColumn,
} from "src/components/shared-components/enum/enumTableHistory";
import {
  Filter,
  FilterValues,
} from "src/components/shared-components/class/general-classes/filter";

import {
  AddressFunction,
  AddressFunctionCategory,
} from "src/components/shared-components/class/component-classes/address-function";
import {
  HiLevelDataFormat,
  SelectionAddressOrAddressFunction,
} from "src/components/shared-components/enum/enumAddress";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import { AddressMethod } from "src/components/shared-components/interfaces/address-method";
import { AddressFunctionService } from "src/service/address-function.service";
import { SmartComponentService } from "src/components/shared-components/service/smart-component.service";
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { environment } from "src/environments/environment.prod";
import { MdSelectDirective } from "../../../../../directive/md-select.directive";
import { PlatformModeContentDirective } from "../../../../../directive/platform-mode-content.directive";
import { Scenario } from "src/components/shared-components/class/general-classes/scenario";
import {
  buildPageEntries,
  paginate,
  PaginationResult,
} from "src/components/shared-components/class/general-classes/pagination";

/**
 * Smartphone in landscape: the header would take the whole height, so the filters start closed
 */
const LANDSCAPE_PHONE_MEDIA_QUERY: string = "(orientation: landscape) and (max-height: 500px)";

/**
 * Window width below which the compact layout is used: the same width under which the
 * popup ("modal-bigger") takes the whole screen. Smartphones in landscape use it too
 */
const COMPACT_LAYOUT_MEDIA_QUERY: string = `(max-width: 767.98px), ${LANDSCAPE_PHONE_MEDIA_QUERY}`;

/**
 * Action that can be chosen for a scenario picked in multi-selection (e.g. execute or stop)
 */
export interface ScenarioActionOption {
  value: string;
  label: string;
  // Ionicons name shown before the label, e.g. "play"
  icon?: string;
  // Theme color variable of the option, without "--", e.g. "green-status": the icon takes the
  // color, the current option in the menu its "-light" variant. The primary when not given
  color?: string;
}

@Component({
    selector: "app-search-address",
    templateUrl: "./search-address.component.html",
    styleUrls: ["./search-address.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonAccordion, IonAccordionGroup, IonButton, IonCheckbox, IonContent, IonFooter, IonHeader, IonIcon, IonInput, IonItem, IonLabel, IonList, IonPopover, IonSearchbar, IonSelect, IonSelectOption, IonText, IonTitle, IonToolbar, MdSelectDirective, FormsModule, ReactiveFormsModule, PlatformModeContentDirective, TranslatePipe, NgTemplateOutlet, NgxDatatableModule, NoContentComponent, PagerComponent]
})
export class SearchAddressComponent
  implements OnInit, OnDestroy, iEvent {
  private readonly _modalCtrl = inject(ModalController);
  private readonly _gatewayService = inject(GatewayService);
  private readonly _alertService = inject(AlertService);
  private readonly _addressService = inject(AddressService);
  private readonly _platform = inject(Platform);
  private readonly _networkService = inject(NetworkService);
  private readonly _translateService = inject(TranslateService);
  private readonly _loaderService = inject(LoaderService);
  private readonly _loggerService = inject(LoggerService);
  private readonly _store = inject(Store);
  private readonly _eventService = inject(EventService);
  private readonly _addressFunctionService = inject(AddressFunctionService);
  private readonly _smartComponentService = inject(SmartComponentService);
  private readonly _elementRef = inject(ElementRef);
  private readonly _themeService = inject(ThemeService);
  private readonly _ngZone = inject(NgZone);

  private _logContext: string = "search-address.component";

  //#region Translation
  private _address: string = "address";
  private _dictionary: string = "dictionary.";
  private _gateway: string = "gateway";
  private _name: string = "name";
  //#endregion

  private _idAll: number = -1;

  // Used by address with any type of value permitted
  private readonly WRITE_METHOD: string = "#WRITE";

  //Used by boolean addresses to indicate to switch the current value
  private readonly SWITCH_METHOD: string = "VALUE NOT";

  private _filterName: string = "";
  private _filterAddress: string = "";
  // The server does not handle the simple data (older version): the complete search is used
  private _isSimpleDataUnsupported: boolean = false;
  private _currentConnectionType: ConnectionType = null;
  private _gateways: Array<Gateway> = null;
  private _gatewayServiceSub: Subscription = null;
  private _networkServiceSubscription: Subscription = null;
  private _apiRequestSubscription: Subscription = null;
  private _apiRequestSubject: Subject<string> = null;
  private _formSubscriptions: Subscription[] = [];
  private _filtersData = new Map<string, string>();
  private _debounceTimer: ReturnType<typeof setTimeout> = null;
  private _numberOfItemsPerPage: number = 100;

  public _eventSubscriptions: Array<Subscription> = null;
  public tableInfo: TableInfo = null;
  public selected: Array<any> = new Array<any>();
  public listAddresses: Array<Address> = null;
  // Row picked in the address functions table, as an array for ngx-datatable
  public selectedAddressFunctionRows: Array<AddressFunction> = [];
  // Narrow window, below the threshold width: one-column tables instead of the desktop ones,
  // collapsible filters, count and deselect on a row of their own
  public isCompactLayout: boolean = false;
  private _compactLayoutQuery: MediaQueryList = null;
  // Smartphone in landscape: the filters accordion starts closed, to leave room for the list
  public isLandscapePhone: boolean = false;
  private _landscapePhoneQuery: MediaQueryList = null;
  public columnMode = ColumnMode;
  public selectionKind = SelectionType;
  // Search in progress: the loader is shown in the table area only, as the loading veil
  // outside Digital Place
  public isLoading: boolean = false;
  // Brand logo of the loading veil
  public srcBrandLogo: string = "";
  // Heights in px of header and rows of the addresses table
  public readonly tableHeaderHeight: number = 50;
  public readonly tableRowHeight: number = 50;
  // Width in px of the table border, set in the stylesheet
  private readonly tableBorderWidth: number = 1;
  // Page of addresses shown, from 1, and the page numbers under the table
  public currentPage: number = 1;
  public pageCount: number = 1;
  public listPageEntry: Array<number> = [];

  public selectedTab: SelectionAddressOrAddressFunction =
    SelectionAddressOrAddressFunction.ADDRESS;
  public filtersAddressFunctions: Filter[] = [];
  public selectCategory: Array<FilterValues> = new Array<FilterValues>();
  public listAddressFunctionOriginal: Array<AddressFunction> =
    new Array<AddressFunction>();
  public listAddressFunction: Array<AddressFunction> =
    new Array<AddressFunction>();
  public listCategory: Array<AddressFunctionCategory> =
    new Array<AddressFunctionCategory>();
  public selectedCategory: number = this._idAll;
  public addressFunctionSearchText: string = "";
  public selectedValue: number = null;
  public selectionType = SelectionAddressOrAddressFunction;
  public addressValue: UntypedFormControl = new UntypedFormControl('');
  public nameValue: UntypedFormControl = new UntypedFormControl('');
  public selectGateway: Array<FilterValues> = new Array<FilterValues>();
  public selectedGateway: string | number = this._idAll;
  public isDigitalPlace: boolean;

  // Picked in multi-selection, kept across searches and pages
  public selectedAddresses: Array<Address> = [];
  public filteredScenarios: Array<Scenario> = [];
  // Page of the scenarios listed that is shown, from 1, and the page numbers under it
  public pagedScenarios: Array<Scenario> = [];
  public scenarioCurrentPage: number = 1;
  public scenarioPageCount: number = 1;
  public scenarioPageEntries: Array<number> = [];
  public scenarioSearchText: string = "";
  public selectedScenarios: Array<Scenario> = [];
  // Action chosen for each picked scenario (by id)
  public scenarioActions: Record<number, string> = {};
  // Scenario whose actions menu is open, and the click that opened it, to place the menu
  public actionMenuScenario: Scenario = null;
  public actionMenuEvent: Event = null;

  @ViewChild("scenarioTable") private _scenarioTable: DatatableComponent;

  @Input() idArea: number = 0;
  // Search also in the child areas of idArea, e.g. the whole building with its apartments
  @Input() includeChildArea: boolean = false;
  @Input() includeVariable: number = -1;
  // Popup title: a translation key (e.g. "dictionary.selAddress") or an already translated text
  @Input() title: string = "";
  // Line under the title, to tell what the picked address is for: translation key or text
  @Input() subtitle: string = "";
  @Input() showAddressFunction: boolean = false;
  @Input() multiSelect: boolean = false;
  // Search with the paged API returning simple data, otherwise with the complete one,
  // which has no paging but returns gateway description and data format too
  @Input() useSimpleData: boolean = true;
  // On confirm, reload the picked addresses with the complete data (methods, last value,
  // limits), which the simple data lacks. BIGStudio forms expect the complete address
  @Input() loadCompleteAddresses: boolean = !environment.isDigitalPlace;
  // Show gateway and format columns next to address and name
  @Input() showAddressDetails: boolean = !environment.isDigitalPlace;
  // Keep the address and name typed in the search fields for the next opening
  @Input() keepSearchFields: boolean = false;
  // Address the picked value will be written on: when set, only compatible addresses are
  // listed. The simple data has no methods: there the accepted values are checked on confirm
  @Input() targetAddress: Address = null;
  // Scenario selection tab: the caller passes the selectable scenarios
  @Input() showScenarioTab: boolean = false;
  @Input() scenarios: Array<Scenario> = [];
  // Actions selectable for each scenario in multi-selection, the first one is the default
  @Input() scenarioActionOptions: Array<ScenarioActionOption> = [];

  /**
   * Number of addresses and scenarios currently picked in multi-selection
   */
  public get selectedCount(): number {
    return this.selectedAddresses.length + this.selectedScenarios.length;
  }

  /**
   * Check if there is something to confirm: in multi-selection anything picked, in single
   * selection the item picked in the tab shown, the only one returned
   */
  public get hasSelectionToConfirm(): boolean {
    if (this.multiSelect) {
      return this.selectedCount > 0;
    }

    switch (this.selectedTab) {
      case this.selectionType.SCENARIO:
        return this.selectedScenarios.length > 0;
      case this.selectionType.ADDRESS_FUNCTION:
        return this.selectedAddressFunctionRows.length > 0;
      default:
        return this.selectedAddresses.length > 0;
    }
  }

  /**
   * Size of the content holding the addresses table
   */
  public get addressContentFlex(): string | null {
    const isTableShown: boolean =
      !this.isCompactLayout && !this.isLoading && this.listAddresses?.length > 0;
    return isTableShown ? this.getTableContentFlex(this.listAddresses.length) : null;
  }

  /**
   * Size of the content holding the scenarios table
   */
  public get scenarioContentFlex(): string | null {
    const isTableShown: boolean =
      !this.isCompactLayout && this.pagedScenarios.length > 0;
    return isTableShown ? this.getTableContentFlex(this.pagedScenarios.length) : null;
  }

  /**
   * Get the flex value for the table content
   * @param rowCount number of rows of the table
   */
  private getTableContentFlex(rowCount: number): string {
    const tableHeight: number =
      this.tableHeaderHeight + rowCount * this.tableRowHeight + 2 * this.tableBorderWidth;
    return `0 1 ${tableHeight}px`;
  }

  /**
   * The gateway column is left out on phones, where there is no room for it
   */
  public get showGatewayColumn(): boolean {
    return this.showAddressDetails && !this.isCompactLayout;
  }

  constructor() {
    this._gatewayService.Initialize();
    this.isDigitalPlace = environment.isDigitalPlace;
    this.srcBrandLogo = this._themeService.getBrandLogoByApplicationTheme();
    this._compactLayoutQuery = window.matchMedia(COMPACT_LAYOUT_MEDIA_QUERY);
    this.isCompactLayout = this._compactLayoutQuery.matches;
    this._compactLayoutQuery.addEventListener("change", this.onCompactLayoutChange);
    this._landscapePhoneQuery = window.matchMedia(LANDSCAPE_PHONE_MEDIA_QUERY);
    this.isLandscapePhone = this._landscapePhoneQuery.matches;
    this._landscapePhoneQuery.addEventListener("change", this.onLandscapePhoneChange);
  }

  /**
   * Close or open the filters when the smartphone is turned
   */
  private onLandscapePhoneChange = (event: MediaQueryListEvent): void => {
    this._ngZone.run(() => {
      this.isLandscapePhone = event.matches;
    });
  };

  /**
   * Switch between the compact layout and the desktop one when the window crosses the
   * threshold width, e.g. resizing the browser or turning a tablet
   */
  private onCompactLayoutChange = (event: MediaQueryListEvent): void => {
    this._ngZone.run(() => {
      this.isCompactLayout = event.matches;
    });
  };

  /**
   * Catch and stop all "Enter" key events to prevent propagation to the parent page.
   * If an input is focused, trigger the search.
   * @param event The keyboard event
   */
  public onHandleEnter(event: any): void {
    // Always stop propagation and prevent default to isolate the modal
    if (event) {
      if (typeof event.stopPropagation === "function") {
        event.stopPropagation();
      }
      if (typeof event.preventDefault === "function") {
        event.preventDefault();
      }
      if (typeof event.stopImmediatePropagation === "function") {
        event.stopImmediatePropagation();
      }
    }

    // Trigger the filter if appropriate (usually on keydown)
    if (!event || event.type === "keydown" || !event.type) {
      const target = event?.target as HTMLElement;
      // We trigger search if focus is in an input or if it's a global action intended for this modal
      if (
        !target ||
        target.tagName === "ION-INPUT" ||
        target.tagName === "INPUT" ||
        target.closest("ion-input")
      ) {
        this.filterAddresses();
      }
    }
  }

  /**
   * Check if something of the kind listed in the tab shown is picked: the deselect button
   * acts on that kind only, while the count in the footer covers both
   */
  public get hasSelectionInTab(): boolean {
    if (this.selectedTab === this.selectionType.SCENARIO) {
      return this.selectedScenarios.length > 0;
    }

    return (
      this.selectedTab === this.selectionType.ADDRESS &&
      this.selectedAddresses.length > 0
    );
  }

  /**
   * Deselect everything of the kind listed in the tab shown: addresses or scenarios
   */
  public deselectAllElements(): void {
    if (this.selectedTab === this.selectionType.SCENARIO) {
      this.selectedScenarios = [];
      this.scenarioActions = {};
      return;
    }

    this.selectedAddresses = [];
  }

  /**
   * Trap Enter key at the window level using capture phase to prevent it from reaching any other part of the app.
   */
  private onHandleGlobalKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Enter") {
      this.onHandleEnter(event);
    }
  };

  /**
   * Select row address
   * @param addressSelect
   */
  public selectRow(addressSelect: Address) {
    if (!this.multiSelect) {
      this.selectedAddresses = [addressSelect];
      return;
    }

    // Each click toggles the address
    this.selectedAddresses = this.isAddressSelected(addressSelect)
      ? this.selectedAddresses.filter((a: Address) => a.id !== addressSelect.id)
      : [...this.selectedAddresses, addressSelect];
  }

  /**
   * Receive the rows selected in the addresses table
   * @param selectedRows rows selected in ngx-datatable
   */
  public onSelectAddressRows(selectedRows: Array<Address>): void {
    this.selectedAddresses = [...selectedRows];
  }

  /**
   * Identify the table rows by address id, so that the selection survives a new search
   * returning the same addresses
   */
  public getAddressIdentity(address: Address): number {
    return address.id;
  }

  /**
   * Get the class of the table rows: the selected row color depends on it, as in the
   * other tables (grey when offline).
   */
  public getRowClass = (): string =>
    this._currentConnectionType != ConnectionType.NONE ? "online" : "offline";

  /**
   * Check if the given address row is currently selected
   * @param address The address to check for selection.
   * @returns `true` if the address is currently selected, `false` otherwise.
   */
  public isAddressSelected(address: Address): boolean {
    return this.selectedAddresses.some((a: Address) => a.id === address.id);
  }

  /**
   * Check if every address of the page shown is picked
   */
  public get areAllAddressesSelected(): boolean {
    return (
      this.listAddresses?.length > 0 &&
      this.listAddresses.every((address: Address) => this.isAddressSelected(address))
    );
  }

  /**
   * Clear the address filters, address, name and gateway, and the results of the last search
   * with their page numbers
   */
  public clearFilters(): void {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }

    this.addressValue.setValue("");
    this.nameValue.setValue("");
    this.selectedGateway = this._idAll;
    this.listAddresses = [];
    this.currentPage = 1;
    this.pageCount = 1;
    this.listPageEntry = [];
  }

  /**
   * Pick every address of the page shown, or drop them all if they are all picked. The
   * addresses picked on the other pages are left as they are
   */
  public toggleAllAddresses(): void {
    if (this.areAllAddressesSelected) {
      const pageIds: Set<number> = new Set(
        this.listAddresses.map((address: Address) => address.id),
      );
      this.selectedAddresses = this.selectedAddresses.filter(
        (address: Address) => !pageIds.has(address.id),
      );
      return;
    }

    this.selectedAddresses = [
      ...this.selectedAddresses,
      ...this.listAddresses.filter((address: Address) => !this.isAddressSelected(address)),
    ];
  }

  /**
   * On input change of the scenario searchbar
   * @param event
   */
  public onInputSearchScenario(event: any): void {
    this.scenarioSearchText = event.detail.value ?? "";
    this.filterScenarios();
  }

  /**
   * Filter the scenarios list by name from the search field.
   */
  private filterScenarios(): void {
    const text: string = this.scenarioSearchText.trim().toLowerCase();
    this.filteredScenarios = text
      ? this.scenarios.filter((scenario: Scenario) =>
          (scenario.name || "").toLowerCase().includes(text),
        )
      : [...this.scenarios];
    this.showScenarioPage(1);
  }

  /**
   * Show the page of scenarios clicked in the page numbers
   * @param page page to show, from 1
   */
  public onClickScenarioPage(page: number): void {
    if (page === this.scenarioCurrentPage || page < 1 || page > this.scenarioPageCount) {
      return;
    }

    this.showScenarioPage(page);
  }

  /**
   * Get the pagination for the scenarios tab
   * @param page page to show, from 1
   */
  private showScenarioPage(page: number): void {
    const pagination: PaginationResult<Scenario> = paginate(
      this.filteredScenarios,
      page,
      this._numberOfItemsPerPage,
    );
    this.pagedScenarios = pagination.listItem;
    this.scenarioCurrentPage = pagination.currentPage;
    this.scenarioPageCount = pagination.pageCount;
    this.scenarioPageEntries = pagination.listPageEntry;
    // The table height follows the rows: once the new height is applied, the table has to
    // measure itself again, it does not notice by itself
    setTimeout(() => this._scenarioTable?.recalculate());
  }

  /**
   * Receive the rows selected in the scenarios table. In multi-selection the newly picked
   * scenarios get the default action, the ones still picked keep the action chosen
   * @param selectedRows rows selected in ngx-datatable
   */
  public onSelectScenarioRows(selectedRows: Array<Scenario>): void {
    this.selectedScenarios = [...selectedRows];
    if (!this.multiSelect) {
      return;
    }

    const scenarioActions: Record<number, string> = {};
    for (const scenario of this.selectedScenarios) {
      scenarioActions[scenario.id] =
        this.scenarioActions[scenario.id] ?? this.scenarioActionOptions[0]?.value;
    }
    this.scenarioActions = scenarioActions;
  }

  /**
   * Identify the scenarios table rows by scenario id
   */
  public getScenarioIdentity(scenario: Scenario): number {
    return scenario.id;
  }

  /**
   * Get the action chosen for a picked scenario, to show it with its icon on the row
   * @param scenario picked scenario. Null while the action menu closes: the scenario it was opened
   * for is dropped at once, but the popover keeps drawing its items until the animation ends
   */
  public getScenarioActionOption(scenario: Scenario | null): ScenarioActionOption | undefined {
    if (!scenario) {
      return undefined;
    }

    return this.scenarioActionOptions.find(
      (option: ScenarioActionOption) => option.value === this.scenarioActions[scenario.id],
    );
  }

  /**
   * Open the menu of the actions for a picked scenario, next to the button clicked. The click
   * does not reach the row, which would drop the scenario
   * @param event click on the action button of the row
   * @param scenario scenario of the row
   */
  public openScenarioActionMenu(event: Event, scenario: Scenario): void {
    event.stopPropagation();
    this.actionMenuEvent = { target: event.currentTarget } as unknown as Event;
    this.actionMenuScenario = scenario;
  }

  /**
   * Choose an action from the menu for the scenario it was opened for
   * @param option action chosen
   */
  public pickScenarioAction(option: ScenarioActionOption): void {
    this.scenarioActions[this.actionMenuScenario.id] = option.value;
    this.closeScenarioActionMenu();
  }

  /**
   * Close the menu of the actions, also when dismissed without a choice
   */
  public closeScenarioActionMenu(): void {
    this.actionMenuScenario = null;
    this.actionMenuEvent = null;
  }

  /**
   * Check if every scenario of the page shown is picked: the header checkbox is then checked
   */
  public get areAllScenariosSelected(): boolean {
    return (
      this.pagedScenarios.length > 0 &&
      this.pagedScenarios.every((scenario: Scenario) =>
        this.isScenarioSelected(scenario),
      )
    );
  }

  /**
   * Pick every scenario of the page shown, or drop them all if they are all picked. The
   * scenarios on the other pages, or hidden by the search text, are left as they are
   */
  public toggleAllScenarios(): void {
    if (this.areAllScenariosSelected) {
      for (const scenario of this.pagedScenarios) {
        delete this.scenarioActions[scenario.id];
      }
      this.selectedScenarios = this.selectedScenarios.filter(
        (scenario: Scenario) => !this.pagedScenarios.includes(scenario),
      );
      return;
    }

    for (const scenario of this.pagedScenarios) {
      if (!this.isScenarioSelected(scenario)) {
        this.selectedScenarios = [...this.selectedScenarios, scenario];
        this.scenarioActions[scenario.id] = this.scenarioActionOptions[0]?.value;
      }
    }
  }

  /**
   * Select a scenario row: in multi-selection each click toggles the scenario,
   * otherwise it replaces the selection.
   * @param scenario
   */
  public selectScenario(scenario: Scenario): void {
    if (!this.multiSelect) {
      this.selectedScenarios = [scenario];
      return;
    }

    if (this.isScenarioSelected(scenario)) {
      this.selectedScenarios = this.selectedScenarios.filter(
        (s: Scenario) => s.id !== scenario.id,
      );
      delete this.scenarioActions[scenario.id];
    } else {
      this.selectedScenarios = [...this.selectedScenarios, scenario];
      this.scenarioActions[scenario.id] = this.scenarioActionOptions[0]?.value;
    }
  }

  /**
   * Check if the given scenario is currently selected
   * @param scenario The scenario to check for selection.
   * @returns `true` if the scenario is currently selected, `false` otherwise.
   */
  public isScenarioSelected(scenario: Scenario): boolean {
    return this.selectedScenarios.some((s: Scenario) => s.id === scenario.id);
  }

  /**
   * On select change category
   * @param event
   */
  public onSelectChangeCategory(event: any) {
    this.selectedCategory = event.detail.value;
    this.filterAddressFunctions();
  }

  /**
   * On input change of the address function searchbar
   * @param event
   */
  public onInputSearchAddressFunction(event: any) {
    this.addressFunctionSearchText = event.detail.value ?? "";
    this.filterAddressFunctions();
  }

  /**
   * Filter address functions by selected category and search text
   */
  private filterAddressFunctions() {
    const searchText = this.addressFunctionSearchText.trim().toLowerCase();

    this.listAddressFunction = this.listAddressFunctionOriginal.filter(
      (item) =>
        (this.selectedCategory == this._idAll ||
          item.idCategory === this.selectedCategory) &&
        (!searchText || item.name?.toLowerCase().includes(searchText)),
    );
  }

  /**
   * Handle change of the selected gateway from the dropdown
   * @param event
   */
  public onSelectChangeGateway(event: any): void {
    this.selectedGateway = event.detail.value;
    this.onInputChangeSetDebounceTime();
  }

  /**
   * Select address function
   * @param addressFunction
   */
  public selectAddressFunction(addressFunction: AddressFunction) {
    this.selectedAddressFunctionRows = addressFunction ? [addressFunction] : [];
  }

  public ngOnInit(): void {
    this.tableInfo = StaticClass.createTableInfo(
      "",
      false,
      SelectionType.single,
      this.createArrayColumns(),
      this._platform.is("android") || this._platform.is("ios"),
      false,
    );
    this.tableInfo.RowHeight = "auto";
    this.tableInfo.IsListInitialized = true;

    this.subscribeApiRequest();
    this.subscribeGatewayService();
    this.subscribeNetworkServiceData();
    this.subscribeEvents();
    this.subscribeToFormChanges();

    if (this.showAddressFunction) {
      this.populateFilterAddressFunctions();
      this.loadAddressFunctions();
    }

    if (this.showScenarioTab) {
      this.filterScenarios();
    }
    if (this.keepSearchFields) {
      //Initialize name and address fields value
      this._smartComponentService.searchAdressesFieldsValue
        .pipe(take(1))
        .subscribe((value) => {
          this.nameValue.setValue(value?.name ?? "");
          this.addressValue.setValue(value?.address ?? "");
        });
    }

    if (this.nameValue.value !== "" || this.addressValue.value !== "") {
      this.filterAddresses();
    }

    // Add global event listener with capture phase to trap Enter key
    window.addEventListener("keydown", this.onHandleGlobalKeyDown, true);
  }

  /**
   * Method to manage the debounce on input, in order to avoid to send too many requests to the API when the user is writing
   */
  public onInputChangeSetDebounceTime(): void {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
    }
    this._debounceTimer = setTimeout(() => {
      this._debounceTimer = null;
      this.filterAddresses();
    }, 3000);
  }

  /**
   * Send data about filter set, when clicking button and on enter on keyboard
   */
  public filterAddresses(): void {
    if (this._debounceTimer) {
      clearTimeout(this._debounceTimer);
      this._debounceTimer = null;
    }
    this.currentPage = 1;
    this.pageCount = 1;
    this.listPageEntry = [];
    this.listAddresses = null;
    // A new search clears the single selection, changing page keeps it
    if (!this.multiSelect) {
      this.selectedAddresses = [];
    }
    this.onFilterRequest(this._filtersData);
  }

  /**
   * Get the pagination for the addresses tab
   * @param page page to show, from 1
   */
  public onClickPage(page: number): void {
    if (page === this.currentPage || page < 1 || page > this.pageCount) {
      return;
    }

    this.currentPage = page;
    this.onFilterRequest(this._filtersData);
  }

  /**
   * Load address functions
   */
  public async loadAddressFunctions() {
    try {
      this.listAddressFunctionOriginal =
        await this._addressFunctionService.getAddressFunction();
      this.filterAddressFunctions();
    } catch (error) {
      await this._alertService.presentAlertSomethingWrong();
    }
  }

  /**
   * Get category by id
   * @param id
   * @returns
   */
  public getCategoryById(id: number): string {
    return this.listCategory?.find((c) => c.id == id)?.name ?? "";
  }

  /**
   * Populate filter address functions
   */
  public async populateFilterAddressFunctions() {
    this.filtersAddressFunctions.push(
      StaticClass.createFilterObject(
        FiltersType.combo,
        "category",
        "dictionary.category",
        "",
        await this.createArrayComboCategory(),
        " ",
      ),
    );
  }

  /**
   * Create array for populate
   * @returns
   */
  public async createArrayComboCategory() {
    try {
      this.listCategory =
        await this._addressFunctionService.getAddressFunctionCategory();

      this.selectedValue = this._idAll;

      if (Array.isArray(this.listCategory)) {
        this.listCategory.forEach((category) => {
          this.selectCategory.push({
            Id: category.id,
            Value: category.name,
          });
        });
      }
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    }

    return this.selectCategory;
  }

  public ngOnDestroy(): void {
    if (this._gatewayServiceSub != null) {
      this._gatewayServiceSub.unsubscribe();
    }
    this._apiRequestSubscription?.unsubscribe();
    this._networkServiceSubscription?.unsubscribe();
    this.unsubscribeEvents();

    this._formSubscriptions?.forEach((sub) => {
      sub.unsubscribe();
    });

    if (this.keepSearchFields) {
      // Emit values in order to keep values on inputs when closing search address skill modal
      this._smartComponentService.searchAdressesFieldsValue.next({
        name: this._filtersData.get("name")?.trim(),
        address: this._filtersData.get("address")?.trim(),
      });
    }

    window.removeEventListener("keydown", this.onHandleGlobalKeyDown, true);
    this._compactLayoutQuery?.removeEventListener("change", this.onCompactLayoutChange);
    this._landscapePhoneQuery?.removeEventListener("change", this.onLandscapePhoneChange);
  }

  //#region Subscribe to event-service observable
  public subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnMakeForceLogout.subscribe(() => {
        this._modalCtrl.dismiss();
      }),
    );
  }

  public unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   * Creates and returns the columns to pass to the table.
   */
  public createArrayColumns(): Column[] {
    let columns: Column[] = [];
    if (!(this._platform.is("ios") || this._platform.is("android"))) {
      columns.push(
        StaticClass.createColumnObj(
          "gatewayDescription",
          this._gateway,
          "",
          ColumnTableType.STRING,
          HideShowColumn.SHOW,
        ),
      );
    }
    columns.push(
      StaticClass.createColumnObj(
        this._address,
        this._address,
        "",
        ColumnTableType.ADDRESS,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        "fullName",
        this._name,
        "",
        ColumnTableType.STRING,
        HideShowColumn.SHOW,
      ),
    );
    columns.push(
      StaticClass.createColumnObj(
        "gatewayDataFormat",
        "format",
        "",
        ColumnTableType.STRING,
        HideShowColumn.SHOW,
      ),
    );
    return columns;
  }

  /**
   * Exit without press confirm
   */
  public onClickCancel(): void {
    this._modalCtrl.dismiss({ action: "cancel" });
  }

  /**
   * On confirm return the selected address if it is selected
   */
  public async onClickConfirm(): Promise<void> {
    const returnsAddresses: boolean =
      this.multiSelect || this.selectedTab == this.selectionType.ADDRESS;
    if (
      returnsAddresses &&
      this.loadCompleteAddresses &&
      this.selectedAddresses.length > 0
    ) {
      const completeAddresses: Array<Address> = await this.getCompleteSelectedAddresses();
      if (!completeAddresses) {
        await this._alertService.presentAlertSomethingWrong();
        return;
      }
      if (!(await this.checkTargetValues(completeAddresses))) {
        return;
      }
      this.selectedAddresses = completeAddresses;
    }

    if (this.multiSelect) {
      // Both tabs are returned, whichever is showing
      this._modalCtrl.dismiss({
        action: "confirm",
        selectedAddresses: this.selectedAddresses,
        selectedScenarios: this.selectedScenarios,
        scenarioActions: this.scenarioActions,
      });
    } else if (this.selectedTab == this.selectionType.SCENARIO) {
      this._modalCtrl.dismiss({
        action: "confirm",
        selectedValue: this.selectedScenarios[0] ?? null,
        selectedType: this.selectionType.SCENARIO,
      });
    } else if (this.selectedTab == this.selectionType.ADDRESS) {
      const selectedValue = this.selectedAddresses[0] ?? null;
      const selectedType = this.selectionType.ADDRESS;

      this._modalCtrl.dismiss({
        action: "confirm",
        selectedValue: selectedValue,
        selectedType: selectedType,
      });
    } else {
      const selectedValue = this.selectedAddressFunctionRows[0] ?? null;
      const selectedType = this.selectionType.ADDRESS_FUNCTION;

      this._modalCtrl.dismiss({
        action: "confirm",
        selectedValue: selectedValue,
        selectedType: selectedType,
      });
    }
  }

  /**
   * Reload the picked addresses with the complete data
   * @returns the complete addresses, null if any of them could not be loaded
   */
  private async getCompleteSelectedAddresses(): Promise<Array<Address> | null> {
    await this._loaderService.presentLoaderWaiting();
    try {
      const results = await Promise.all(
        this.selectedAddresses.map((address: Address) =>
          this._addressService.getAddressById(address.id),
        ),
      );
      // An address not found and a failed call both come back without data
      const completeAddresses: Array<Address> = results.map((result) =>
        Array.isArray(result) ? result[0] : result,
      );
      return completeAddresses.every((address: Address) => !!address)
        ? completeAddresses
        : null;
    } finally {
      await this._loaderService.dismissLoaderWaiting();
    }
  }

  /**
   * Check that the picked addresses only provide values accepted by the target address,
   * telling the user which one does not
   * @param addresses complete addresses, with their methods
   */
  private async checkTargetValues(addresses: Array<Address>): Promise<boolean> {
    if (!this.targetAddress) {
      return true;
    }

    const incompatibleAddress: Address = addresses.find(
      (address: Address) => !this.takesTargetValues(address),
    );
    if (!incompatibleAddress) {
      return true;
    }

    await this._alertService.presentAlertOk(
      this._translateService.instant("dictionary.attention"),
      "",
      this._translateService.instant("dictionary.addressNotCompatible", {
        address: incompatibleAddress.address,
      }),
    );
    return false;
  }

  /**
   * Subscribe to name and address input changes
   */
  private subscribeToFormChanges(): void {
    this._formSubscriptions.push(
      this.nameValue.valueChanges.subscribe((value) => {
        this.setFilters("name", value);
      }),
    );

    this._formSubscriptions.push(
      this.addressValue.valueChanges.subscribe((value) => {
        this.setFilters("address", value);
      }),
    );
  }

  /**
   * Set value of filters
   * @param filterName name of the filter
   * @param value value of the filter
   */
  private setFilters(filterName: string, value: string): void {
    this._filtersData.set(filterName, value.trim());
  }

  /**
   * Load gateways select
   */
  private subscribeGatewayService(): void {
    this._gatewayServiceSub = this._store
      .select(listGateway)
      .pipe(
        filter(
          (listGateway: GenericStoreApiData) =>
            listGateway !== null && listGateway !== undefined,
        ),
        filter((listGateway: GenericStoreApiData) => listGateway?.loaded),
        filter(
          (listGatewayStore: GenericStoreApiData) =>
            !(
              listGatewayStore.errorGeneric ||
              listGatewayStore.errorNotYetImplemented
            ),
        ),
        tap((listGatewayStore: GenericStoreApiData) => {
          if (listGatewayStore.isOldDataInUse) {
            this._loggerService.log(
              LogLevel.LOG_INFO,
              this._logContext,
              "_store.select(listGateway)",
              "subscribeGatewayService()",
              "We are using old data!!",
            );
          }
        }),
      )
      .subscribe((listGateway: GenericStoreApiData) => {
        if (!listGateway.errorGeneric) {
          let arrayToSort = [...(listGateway.data?.data ?? [])];
          arrayToSort.sort((g1, g2) =>
            g1.description.localeCompare(g2.description),
          );
          this._gateways = arrayToSort || [];
          this.initFilterFields();
        } else {
          this.handleApiResponseError();
        }
      });
  }

  private async handleApiResponseError(): Promise<void> {
    await this._alertService.presentAlertSomethingWrong();
  }

  /**
   * Creates the array containing the filters to be used for queries.
   */
  private initFilterFields(): void {
    this.selectGateway = this.createArrayComboGateway();

    this.tableInfo.Filters = [];
    this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.text,
        this._address,
        this._dictionary + this._address,
        "",
        [],
        "",
      ),
    );
    this.tableInfo.Filters.push(
      StaticClass.createFilterObject(
        FiltersType.text,
        this._name,
        this._dictionary + this._name,
        "",
        [],
        "",
      ),
    );
  }

  /**
   * Create the array for the gateway combobox present in the filters.
   */
  private createArrayComboGateway(): Array<FilterValues> {
    let selectArray: Array<FilterValues> = new Array<FilterValues>();
    selectArray.push({
      Id: this._idAll,
      Value: "dictionary.all",
    });
    for (let i = 0; i < this._gateways.length; i++) {
      selectArray.push({
        Id: this._gateways[i].instanceGUID,
        Value: this._gateways[i].description,
      });
    }
    return selectArray;
  }

  /**
   * Filter data
   * @param filterData
   */
  public async onFilterRequest(filterData: Map<string, string>): Promise<void> {
    // Parameters joined at the end: with no name or address filter yet, appending each
    // one with its own "&" left the query starting with "&", hence "&&" in the URL
    const queryParams: Array<string> = [];

    filterData.forEach((value, key) => {
      if (key == "name" && value.trim() != "") {
        this._filterName = value;
      }
      if (key == "address" && value.trim() != "") {
        this._filterAddress = value;
      }
      queryParams.push(key + "=" + encodeURIComponent(value));
    });
    if (this.idArea > 0 && this.isDigitalPlace) {
      queryParams.push("idArea=" + encodeURIComponent(this.idArea));
      if (this.includeChildArea) {
        queryParams.push("includeChildArea=true");
      }
    }
    if (this.includeVariable > -1) {
      queryParams.push("includeVariable=" + encodeURIComponent(this.includeVariable));
    }
    if (this.selectedGateway != null && this.selectedGateway != this._idAll) {
      queryParams.push("InstanceGUID=" + encodeURIComponent(this.selectedGateway));
    }
    queryParams.push(
      `nRow=${this._numberOfItemsPerPage}`,
      `offset=${(this.currentPage - 1) * this._numberOfItemsPerPage}`,
    );

    this._apiRequestSubject.next(queryParams.join("&"));
  }

  /**
   * Initialize observable and subscription for loading skill addresses on search
   */
  private subscribeApiRequest(): void {
    this._apiRequestSubject = new Subject();
    this._apiRequestSubscription = this._apiRequestSubject
      .pipe(
        tap(() => {
          this.isLoading = true;
        }),
        switchMap((query: string) =>
          // The catchError must stay INSIDE the switchMap: if it is placed on the outer
          // chain, an API error unsubscribes "_apiRequestSubject" for good and no further
          // search can be started until the modal is reopened.
          this.searchAddresses(query).pipe(
            catchError(async () => {
              this.isLoading = false;
              await this._alertService.presentAlertSomethingWrong();
              return null;
            }),
          ),
        ),
      )
      .subscribe(async (resultAddresses: any) => {
        const items: Address[] = resultAddresses?.items ?? [];
        const total: number = resultAddresses?.total ?? 0;
        if (this.isSimpleSearch && this.showAddressDetails) {
          await this.fillAddressDetails(items);
        }
        // A page may show fewer rows than the page size: the compatibility filter
        // discards the addresses whose format cannot be written on the target one
        this.listAddresses = this.keepCompatibleAddresses(items);
        // The complete API has no paging: everything comes in a single page
        this.pageCount = this.isSimpleSearch
          ? Math.max(1, Math.ceil(total / this._numberOfItemsPerPage))
          : 1;
        this.listPageEntry = buildPageEntries(this.currentPage, this.pageCount);
        this.isLoading = false;
      });
  }

  /**
   * Check if the search uses the simple data: asked by the caller and handled by the server
   */
  private get isSimpleSearch(): boolean {
    return this.useSimpleData && !this._isSimpleDataUnsupported;
  }

  /**
   * Search the addresses with the simple data or with the complete one. A server older than
   * the simple data ignores the parameter and answers in the complete format, without
   * "items": the search is then made again with the complete API, also for the next ones
   * @param query filters and page of the simple search
   */
  private searchAddresses(query: string): Observable<{ items: Address[]; total: number }> {
    if (!this.isSimpleSearch) {
      return from(this.getAllAddresses());
    }

    return this._addressService.getAddressesForSmartComponent(query).pipe(
      switchMap((result: any) => {
        if (result != null && !Array.isArray(result.items)) {
          this._isSimpleDataUnsupported = true;
          this.currentPage = 1;
          return from(this.getAllAddresses());
        }
        return of(result);
      }),
    );
  }

  /**
   * Get all the addresses matching the filters from the complete API, which has no paging:
   * the result has the same shape as the paged one, with every address in a single page
   */
  private async getAllAddresses(): Promise<{ items: Address[]; total: number }> {
    const addressSearch: iAddressSearch = {
      Instanceguid:
        this.selectedGateway != null && this.selectedGateway != this._idAll
          ? this.selectedGateway.toString()
          : "000",
      Address: this._filtersData.get("address") ?? "",
      Name: this._filtersData.get("name") ?? "",
      Language: this._translateService.getCurrentLang(),
    };

    // An empty result and a failed call both come back without data: listed as no addresses
    const result = await this._addressService.getAddressesByInstanceGUIDAndAddressAndName(
      addressSearch,
    );
    const items: Address[] = result == null ? [] : Array.isArray(result) ? result : [result];
    // The server sends the DB gateway description (ex. "BIG Omnia"): show the same name used in the rest of the app
    items.forEach((address: any) => {
      const gateway = this._gateways?.find((g: Gateway) => g.instanceGUID === address.instanceGUID);
      if (gateway) {
        address.gatewayDescription = gateway.description;
      }
    });
    return { items: items, total: items.length };
  }

  /**
   * Fill gateway description and data format, which the simple data lacks: the first from
   * the gateways list, the second from the device kind of the address
   * @param addresses addresses got from the simple search
   */
  private async fillAddressDetails(addresses: Array<Address>): Promise<void> {
    let gatewayDataFormats: Map<number, string>;
    try {
      gatewayDataFormats = await this._smartComponentService.getGatewayDataFormats();
    } catch {
      // Without device kinds the format column stays empty, the search still works
      gatewayDataFormats = new Map<number, string>();
    }

    for (const address of addresses as Array<any>) {
      address.gatewayDescription =
        this._gateways?.find(
          (gateway: Gateway) => gateway.instanceGUID === address.instanceGUID,
        )?.description ?? "";
      address.gatewayDataFormat = gatewayDataFormats.get(address.idDeviceKind) ?? "";
    }
  }

  /**
   * Keep only the addresses compatible with the target address
   */
  private keepCompatibleAddresses(addresses: Array<Address>): Array<Address> {
    if (!this.targetAddress) {
      return addresses;
    }

    // Return only the addresses that are compatible with the target address. The simple
    // data has no methods: the values are checked on confirm, on the complete address
    return addresses.filter(
      (address: Address) =>
        this.isFormatWritable(
          address.hiLevelDataFormat,
          this.targetAddress.hiLevelDataFormat,
        ) &&
        (this.isSimpleSearch || this.takesTargetValues(address)),
    );
  }

  /**
   * Check if the values the address declares are all accepted by the target address
   * @param address address to check, with its methods
   */
  private takesTargetValues(address: Address): boolean {
    // Check if there are any values to accept. If the array is empty any value is accepted,
    // otherwise only the values in the array are accepted.
    const acceptedValues: Array<string> = this.acceptsAnyValue(this.targetAddress)
      ? []
      : this.declaredValues(this.targetAddress);

    // A boolean also takes the switch method
    if (acceptedValues.length && this.isBoolean(this.targetAddress)) {
      acceptedValues.push(this.SWITCH_METHOD);
    }

    return (
      !acceptedValues.length ||
      this.takesOnlyValues(new Address(address), acceptedValues)
    );
  }

  /**
   * Take the values the address can provide and check if they are all among the accepted
   * values: 0 and 1 for a boolean, whatever its methods, the declared ones otherwise.
   * An address declaring no values is accepted only when it is of the same device kind
   * as the target (e.g. the status address of the one written), so its values are the same.
   */
  private takesOnlyValues(
    address: Address,
    acceptedValues: Array<string>,
  ): boolean {
    const values: Array<string> = this.isBoolean(address)
      ? ["0", "1"]
      : this.declaredValues(address);

    if (!values.length) {
      return this.isSameDeviceKind(address, this.targetAddress);
    }

    return values.every((value: string) => acceptedValues.includes(value));
  }

  /**
   * Check if the two addresses are of the same device kind (e.g. both 20.102 HVAC mode)
   */
  private isSameDeviceKind(address: Address, otherAddress: Address): boolean {
    return !!address.idDeviceKind && address.idDeviceKind === otherAddress.idDeviceKind;
  }

  /**
   * Check compatibility by hiLevelDataFormat
   */
  private isFormatWritable(sourceFormat: string, targetFormat: string): boolean {
    const stringFormat: string =
      EnumStringConverter.hiLevelDataFormatToStringEnum(
        HiLevelDataFormat.STRING,
      );
    const floatFormat: string =
      EnumStringConverter.hiLevelDataFormatToStringEnum(HiLevelDataFormat.FLOAT);
    const integerFormat: string =
      EnumStringConverter.hiLevelDataFormatToStringEnum(
        HiLevelDataFormat.INTEGER,
      );

    return (
      !sourceFormat ||
      !targetFormat ||
      sourceFormat === targetFormat ||
      targetFormat === stringFormat ||
      (targetFormat === floatFormat && sourceFormat === integerFormat)
    );
  }

  /**
   * Recognise the addresses holding a boolean value by their data format: they are the
   * ones that also take the switch method.
   */
  private isBoolean(address: Address): boolean {
    return (
      address.hiLevelDataFormat ===
      EnumStringConverter.hiLevelDataFormatToStringEnum(
        HiLevelDataFormat.BOOLEAN,
      )
    );
  }

  /**
   * Check if the address accepts any value through its methods
   */
  private acceptsAnyValue(address: Address): boolean {
    return address
      .SplitMethods(
        address.loadMethodStringByLanguage(this._translateService.getCurrentLang()),
      )
      .some(
        (method: AddressMethod) =>
          !method.value || method.value === this.WRITE_METHOD,
      );
  }

  /**
   * Get the values an address declares through its methods, excluding #WRITE
   * (e.g. ON=1|OFF=0 → ["1", "0"])
   */
  private declaredValues(address: Address): Array<string> {
    if (!address) {
      return [];
    }

    return address
      .SplitMethods(
        address.loadMethodStringByLanguage(this._translateService.getCurrentLang()),
      )
      .map((method: AddressMethod) => method.value)
      .filter((value: string) => !!value && value !== this.WRITE_METHOD);
  }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this._currentConnectionType = currentConnectionType;
          if (this.listAddresses !== null)
            this.listAddresses = [...this.listAddresses];
        },
      );
  }
}
